/**
 * Export Table Data to Excel - HEATWAVE REPORTS
 * Handles Excel generation with conditional formatting for heatwave disaster reports
 */

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { 
  formatDate, 
  transformDataForExcel, 
  transformMISDataForExcel, 
  transformMISDistrictData,
  transformMISStateData,
  transformMISStateDatewiseData,
  formatDateForMISExcel, 
  getTimestamp 
} from "./excelUtilsHeatwave";

/**
 * Apply conditional formatting for Heatwave Report Status
 * RED: No reports submitted (report_submitted === 0)
 * GREEN: All reports submitted (report_not_submitted === 0)
 * YELLOW: Partial submission
 */

const applyHeatWaveFormatting = (worksheet, columns) => {
  try {
    let reportSubmittedColIndex = -1;
    let reportNotSubmittedColIndex = -1;
    
    // Find column indices
    columns.forEach((col, index) => {
      if (col.label === 'Report Submitted') reportSubmittedColIndex = index + 1;
      else if (col.label === 'Report Not Submitted') reportNotSubmittedColIndex = index + 1;
    });

    console.log('Searching for columns - Found indices:', `Submitted=${reportSubmittedColIndex}, NotSubmitted=${reportNotSubmittedColIndex}`);
    console.log('Available columns:', columns.map(c => c.label));

    // Validation check
    if (reportSubmittedColIndex === -1 || reportNotSubmittedColIndex === -1) {
      console.error('Could not find required columns for heatwave formatting');
      return;
    }

    console.log('Applying heatwave formatting on columns:', `SubmittedCol=${reportSubmittedColIndex}, NotSubmittedCol=${reportNotSubmittedColIndex}`);
    let formattedRowCount = 0;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return; // Skip header row

      try {
        const reportSubmittedCell = row.getCell(reportSubmittedColIndex);
        const reportNotSubmittedCell = row.getCell(reportNotSubmittedColIndex);
        
        const reportSubmittedValue = reportSubmittedCell.value;
        const reportNotSubmittedValue = reportNotSubmittedCell.value;

        // Determine color based on submission status
        const shouldHighlightRed = (reportSubmittedValue === 0 || reportSubmittedValue === '0');
        const shouldHighlightGreen = (reportNotSubmittedValue === 0 || reportNotSubmittedValue === '0');

        row.eachCell({ includeEmpty: true }, (cell) => {
          try {
            if (shouldHighlightRed) {
              // RED: No reports submitted
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF0000' } };
              // ✅ REMOVED: cell.font = { color: { argb: 'FFFFFFFF' } }; // Keep black text for readability
            } else if (shouldHighlightGreen) {
              // GREEN: All reports submitted
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00FF00' } };
            } else {
              // YELLOW: Partial submission
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
            }
          } catch (cellError) {
            console.error(`Error formatting cell in row ${rowNumber}:`, cellError);
          }
        });

        formattedRowCount++;
      } catch (rowError) {
        console.error(`Error formatting row ${rowNumber}:`, rowError);
      }
    });

    console.log(`Successfully formatted ${formattedRowCount} rows with heatwave colors`);
  } catch (error) {
    console.error('Fatal error in applyHeatWaveFormatting:', error);
    console.error('Stack trace:', error.stack);
    // Don't throw - allow Excel generation to continue without formatting
  }
};




/**
 * Main Excel export function for Daily Heatwave Reports
 */
export const generateExcelReport = async (reportData) => {
  const { data, columnOrder, toolName, date, filename } = reportData;

  if (!data || data.length === 0) {
    alert("No data available to export.");
    return;
  }

  try {
    const { columns, transformedData } = transformDataForExcel(data, columnOrder);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    // Row 1: Column Headers
    worksheet.addRow(columns.map((col) => col.label));

    // Row 2+: Data rows
    transformedData.forEach((row) => {
      const rowData = columns.map((col) => row[col.label]);
      worksheet.addRow(rowData);
    });

    // Format headers
    worksheet.getRow(1).eachCell((cell, colNumber) => {
      const alignment = colNumber === 1 ? "left" : "right";
      cell.alignment = { horizontal: alignment, vertical: "middle" };
    });

    // Format data rows
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) {
        row.eachCell((cell, colNumber) => {
          const alignment = colNumber === 1 ? "left" : "right";
          cell.alignment = { horizontal: alignment, vertical: "middle" };
        });
      }
    });

    // Conditional formatting for status reports
    if (toolName === "heatwavedailyreportstatus") {
      applyHeatWaveFormatting(worksheet, columns);
    }

    // Auto-adjust column widths
    columns.forEach((col, index) => {
      const columnLetter = String.fromCharCode(65 + index);
      let maxLength = col.label.length;

      transformedData.forEach((row) => {
        const cellValue = String(row[col.label] || "");
        maxLength = Math.max(maxLength, cellValue.length);
      });

      worksheet.getColumn(columnLetter).width = Math.min(maxLength + 2, 40);
    });

    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const timestamp = getTimestamp();
    let baseFilename;

    if (toolName === "heatwave_daily_report_status") {
      baseFilename = "DailyHeatWaveReportStatus";
    } else {
      baseFilename = filename || "Heat_Wave_Report";
    }

    const finalFilename = `${baseFilename}_${timestamp}.xlsx`;
    saveAs(blob, finalFilename);
    console.log(`✅ Heatwave Excel file generated: ${finalFilename}`);
  } catch (error) {
    console.error("Heatwave Excel generation error:", error);
    throw error;
  }
};

/**
 * MIS Heatwave Report Excel Export (handles all 4 types)
 */
export const generateMISExcelReport = async (reportData, reportType) => {
  const { data, fromDate, toDate, filename, selectedView, displayData } = reportData;
  const type = reportType || selectedView || 'dist_date';

  console.log("🔍 MIS Heatwave Report Excel Generation:", { type, fromDate, toDate });

  if (!data || typeof data !== 'object') {
    alert("No data available to export.");
    return;
  }

  try {
    let transformedResult;
    let reportName;

    switch (type) {
      case 'dist_date':
        transformedResult = transformMISDataForExcel(data);
        reportName = 'DIST_DATE_HEAT_WAVE_REPORT';
        break;
      case 'dist':
        transformedResult = transformMISDistrictData(data);
        reportName = 'DIST_HEAT_WAVE_REPORT';;
        break;
      case 'state':
        transformedResult = transformMISStateData(data);
        reportName = 'STATE_HEAT_WAVE_REPORT';
        break;
      case 'state_date':
        transformedResult = transformMISStateDatewiseData(data);
        reportName = 'STATE_DATE_HEATWAVE_REPORT';
        break;
      default:
        transformedResult = transformMISDataForExcel(data);
        reportName = 'HEATWAVE_REPORT';
    }

    const { columns, transformedData } = transformedResult;

    if (transformedData.length === 0) {
      alert("No data available to export.");
      return;
    }

    console.log(`✅ Transformed ${transformedData.length} heatwave rows`);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    // Row 1-3: Metadata
    const metadataRow1 = worksheet.addRow([`Report Name: ${reportName}`]);
    metadataRow1.getCell(1).font = { bold: true };
    metadataRow1.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
    worksheet.mergeCells(`A1:${String.fromCharCode(64 + columns.length)}1`);

    const metadataRow2 = worksheet.addRow([`From Date: ${fromDate || 'N/A'}`]);
    metadataRow2.getCell(1).font = { bold: true };
    metadataRow2.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
    worksheet.mergeCells(`A2:${String.fromCharCode(64 + columns.length)}2`);

    const metadataRow3 = worksheet.addRow([`To Date: ${toDate || 'N/A'}`]);
    metadataRow3.getCell(1).font = { bold: true };
    metadataRow3.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
    worksheet.mergeCells(`A3:${String.fromCharCode(64 + columns.length)}3`);

    // Row 4: Headers
    const headerRow = worksheet.addRow(columns.map((col) => col.label));
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
    });

    // Row 5+: Data rows
    transformedData.forEach((row) => {
      const rowData = columns.map((col) => {
        const value = row[col.label];
        return value !== undefined && value !== null ? value : "";
      });
      const dataRow = worksheet.addRow(rowData);
      dataRow.eachCell((cell) => {
        cell.alignment = { horizontal: "left", vertical: "middle" };
      });
    });


    // Fixed column width: 1.70 cm ≈ 9 character units
    columns.forEach((col, colIndex) => {
      worksheet.getColumn(colIndex + 1).width = 9;
    });

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toISOString().split('T')[1].split('.')[0].replace(/:/g, '');

    const filenameMap = {
      'dist_date': `MIS_Dist_Datewise_HeatWave_Report_${dateStr}_${timeStr}.xlsx`,
      'dist': `MIS_Dist_HeatWave_Report_${dateStr}_${timeStr}.xlsx`,
      'state': `MIS_State_HeatWave_Report_${dateStr}_${timeStr}.xlsx`,
      'state_date': `MIS_State_Datewise_HeatWave_Report_${dateStr}_${timeStr}.xlsx`
    };

    const finalFilename = filenameMap[type] || `${filename}_${dateStr}_${timeStr}.xlsx`;
    saveAs(blob, finalFilename);
    console.log(`✅ MIS Heatwave Excel file generated: ${finalFilename}`);
  } catch (error) {
    console.error("❌ MIS Heatwave Excel generation error:", error);
    alert(`Excel generation failed: ${error.message}`);
    throw error;
  }
};

/**
 * Router function - Auto-detects all 4 heatwave report types
 */
export const generateReportExcel = async (reportData) => {
  const { toolName, selectedView } = reportData;
  console.log("🔀 generateReportExcel (Heatwave) called with:", { toolName, selectedView });

  const reportType = selectedView || toolName;

  const misReportTypes = ['dist_date', 'dist', 'state', 'state_date',
    'heatwave_mis_district_datewise', 'heatwave_mis_district',
    'heatwave_mis_state', 'heatwave_mis_state_datewise'];

  if (misReportTypes.includes(reportType)) {
    const viewMap = {
      'heatwave_mis_district_datewise': 'dist_date',
      'heatwave_mis_district': 'dist',
      'heatwave_mis_state': 'state',
      'heatwave_mis_state_datewise': 'state_date'
    };

    const type = viewMap[reportType] || reportType;
    console.log(`✅ Routing to MIS Heatwave Report: ${type}`);
    return await generateMISExcelReport(reportData, type);
  } else {
    console.log("✅ Routing to generateExcelReport (daily heatwave report)");
    return await generateExcelReport(reportData);
  }
};
